import React, { useEffect, useRef, useState } from 'react'
import { Autocomplete, Box, Divider, InputAdornment, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import debounce from 'lodash/debounce'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

import { apiZodios } from '~api/apiZodios'
import { LocationIcon } from '~assets/icons'
import { Button, TextField } from '~components/common'
import { Icon } from '~components/icons'
import { MAPBOX_TOKEN } from '~config'
import { useGeolocationPermission } from '~hooks/permissions'
import { type GeoPoint, PermissionState } from '~types'
import { sanitizeAndProcessLinks } from '~utils/htmlUtils'

export interface ILocationEntryInlineAddress {
  address: string
  location: GeoPoint
}

interface LocationEntryInlineProps {
  onSave: (inputAddress: ILocationEntryInlineAddress) => void
  initialPosition: GeoPoint
  message: string
  onMapOpen?: () => void
  isMobile?: boolean
}

interface IMapboxContextItem {
  id: string
  text: string
}

interface IMapboxFeature {
  text: string
  context?: IMapboxContextItem[]
}

interface IMapboxGeocodeResponse {
  features: IMapboxFeature[]
}

const LocationEntryInline: React.FC<LocationEntryInlineProps> = ({
  onSave,
  initialPosition,
  message,
  onMapOpen,
  isMobile,
}) => {
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<mapboxgl.Map | null>(null)
  const marker = useRef<mapboxgl.Marker | null>(null)

  const [addressInput, setAddressInput] = useState('')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isGettingLocation, setIsGettingLocation] = useState(false)
  const [open, setOpen] = useState(false)
  const [highlightedOption, setHighlightedOption] = useState<string | null>(null)

  const [markerPosition, setMarkerPosition] = useState<GeoPoint>(initialPosition)
  const [addressFields, setAddressFields] = useState({
    street: '',
    city: '',
    state: '',
    zip: '',
  })
  const [isMapInitialized, setIsMapInitialized] = useState(false)

  const [showMap, setShowMap] = useState(false)

  const { state: geolocationPermission, canUserFix } = useGeolocationPermission()

  const searchAddress = useRef(
    debounce(async (value: string, position: GeoPoint) => {
      if (value.length < 3) {
        setSuggestions([])
        setIsSearching(false)
        setOpen(false)
        return
      }

      setIsSearching(true)
      try {
        const { addresses: resultsAddresses } = await apiZodios.contacts_searchAddresses({
          address: value,
          location: { lat: position.lat, lon: position.lon },
        })
        setSuggestions(resultsAddresses.slice(0, 5))
        setOpen(resultsAddresses.length > 0)
      } catch (error) {
        console.error('Error searching for address:', error)
        setSuggestions([])
        setOpen(false)
      } finally {
        setIsSearching(false)
      }
    }, 300),
  ).current

  const handleAddressSelect = async (address: string | null): Promise<void> => {
    if (!address) return

    try {
      const response = await apiZodios.contacts_quickCreateAddress({
        address: address,
      })

      if (response.address) {
        const addr = response.address
        setAddressFields({
          street: addr.street,
          city: addr.city,
          state: addr.state,
          zip: addr.zip,
        })

        if (addr.location) {
          setMarkerPosition(addr.location)
          updateMapLocation(addr.location)
        }
        setAddressInput(address)
      }
    } catch (error) {
      console.error('Error creating quick address:', error)
    }
  }

  // Parse coordinates from text input (e.g., "lat,lon" or "lat, lon")
  const parseCoordinates = (input: string): GeoPoint | null => {
    const coordPattern = /^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/
    const match = input.trim().match(coordPattern)

    if (match) {
      const lat = parseFloat(match[1])
      const lon = parseFloat(match[2])

      if (lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        return { lat, lon }
      }
    }
    return null
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleInputChange = (_: any, value: string, reason: string): void => {
    setAddressInput(value)

    if (!value) {
      searchAddress.cancel()
      setAddressFields({ street: '', city: '', state: '', zip: '' })
      setSuggestions([])
      setOpen(false)
      return
    }

    // Check if input looks like coordinates
    const coords = parseCoordinates(value)
    if (coords) {
      setMarkerPosition(coords)
      updateMapLocation(coords)
      reverseGeocode(coords)
      setSuggestions([]) // Clear suggestions for coordinate input
      setOpen(false)
    } else if (reason === 'input') {
      // Only search when user is actually typing, not when selecting
      searchAddress(value, markerPosition)
    }
  }

  // Reverse geocode coordinates to get address
  const reverseGeocode = async (position: GeoPoint): Promise<string> => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${position.lon},${position.lat}.json?access_token=${MAPBOX_TOKEN}`,
      )
      const data: IMapboxGeocodeResponse = await response.json()

      if (data.features && data.features.length > 0) {
        const place = data.features[0]
        const street = place.text || ''
        const city =
          place.context?.find((c: IMapboxContextItem) => c.id.startsWith('place.'))?.text || ''
        const state =
          place.context?.find((c: IMapboxContextItem) => c.id.startsWith('region.'))?.text || ''
        const zip =
          place.context?.find((c: IMapboxContextItem) => c.id.startsWith('postcode.'))?.text || ''
        setAddressFields({ street, city, state, zip })
        return `${street}, ${city}, ${state} ${zip}`.trim()
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error)
    }
    return ''
  }

  const updateMapLocation = (position: GeoPoint): void => {
    if (map.current && marker.current) {
      map.current.setCenter([position.lon, position.lat])
      marker.current.setLngLat([position.lon, position.lat])
    }
  }

  const handleSave = (): void => {
    onSave({
      address: displayAddress,
      location: markerPosition,
    })
  }

  const handleUseCurrentLocation = (): void => {
    if (navigator.geolocation) {
      setIsGettingLocation(true)
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          const newPosition = { lat: latitude, lon: longitude }
          setMarkerPosition(newPosition)
          updateMapLocation(newPosition)
          reverseGeocode(newPosition).then((addressText) => {
            onSave({
              address: addressText,
              location: newPosition,
            })
          })
          setIsGettingLocation(false)
        },
        (error) => {
          console.error('Error getting location:', error)
          setIsGettingLocation(false)
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        },
      )
    } else {
      console.error('Geolocation is not supported by your browser.')
    }
  }

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || isMapInitialized) return

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [markerPosition.lon, markerPosition.lat],
      zoom: 13,
      accessToken: MAPBOX_TOKEN,
    })

    marker.current = new mapboxgl.Marker({
      draggable: true,
      color: '#1976d2',
    })
      .setLngLat([markerPosition.lon, markerPosition.lat])
      .addTo(map.current)

    // Handle marker drag
    marker.current.on('dragend', () => {
      if (marker.current) {
        const lngLat = marker.current.getLngLat()
        const newPosition = { lon: lngLat.lng, lat: lngLat.lat }
        setMarkerPosition(newPosition)
        reverseGeocode(newPosition).then((addressText) => {
          if (!addressText) {
            setAddressInput(`${newPosition.lat}, ${newPosition.lon}`)
          }
        })
      }
    })

    map.current.on('load', () => {
      map.current?.resize()
      setIsMapInitialized(true)
    })

    return (): void => {
      if (marker.current) marker.current.remove()
      if (map.current) map.current.remove()
      setIsMapInitialized(false)
    }
  }, [showMap])

  // Update marker position when markerPosition changes
  useEffect(() => {
    if (marker.current && isMapInitialized) {
      marker.current.setLngLat([markerPosition.lon, markerPosition.lat])
      if (map.current) {
        map.current.setCenter([markerPosition.lon, markerPosition.lat])
      }
    }
  }, [markerPosition, isMapInitialized])

  useEffect(() => {
    if (showMap) {
      requestAnimationFrame(() => {
        onMapOpen?.()
      })
    }
  }, [showMap])

  const isValidLocation =
    (markerPosition.lat !== 0 || markerPosition.lon !== 0) &&
    !!(addressInput || addressFields.street)
  const displayAddress = addressFields.street
    ? `${addressFields.street}, ${addressFields.city}, ${addressFields.state} ${addressFields.zip}`.trim()
    : addressInput

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '500px',
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2 }}>
        <div
          dangerouslySetInnerHTML={{
            __html: sanitizeAndProcessLinks(message),
          }}
        />
      </Box>
      <Divider />
      <Box sx={{ p: 2, gap: 2, display: 'flex' }}>
        {/* Use Current Location Button */}
        {canUserFix && (
          <Button
            variant='outlined'
            color='secondary'
            startIcon={<Icon name='MyLocation' aria-hidden='true' />}
            onClick={handleUseCurrentLocation}
            disabled={isGettingLocation || geolocationPermission === PermissionState.BROWSER_DENIED}
            fullWidth
            sx={{
              whiteSpace: 'nowrap',
              '& .MuiButton-startIcon': {
                marginRight: '8px',
              },
              fontSize: '0.875rem', // Slightly smaller font to fit better
            }}
          >
            {isGettingLocation
              ? 'Getting Location...'
              : isMobile
                ? 'Use Location'
                : 'Use Current Location'}
          </Button>
        )}

        <Button
          variant='outlined'
          color='secondary'
          onClick={() => setShowMap(!showMap)}
          fullWidth
          sx={{
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            '& .MuiButton-startIcon': {
              marginRight: '8px',
            },
            fontSize: '0.875rem', // Slightly smaller font to fit better
          }}
        >
          Enter Address
        </Button>
      </Box>
      {!showMap && geolocationPermission === PermissionState.BROWSER_DENIED && (
        <Box sx={{ paddingLeft: 2, paddingRight: 2, paddingBottom: 1, mt: -1 }}>
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
            }}
          >
            Please allow location in your browser settings to use your current location.
          </Typography>
        </Box>
      )}
      {showMap && (
        <Box sx={{ p: 2, mt: -1 }}>
          {/* Input Section */}
          <Autocomplete
            freeSolo
            options={suggestions}
            value={addressInput}
            inputValue={addressInput}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={(_, reason) => {
              // Close only on genuine dismissals so arrow-key navigation keeps the list open
              if (
                reason === 'escape' ||
                reason === 'blur' ||
                reason === 'toggleInput' ||
                reason === 'selectOption'
              ) {
                setOpen(false)
                setHighlightedOption(null)
              }
            }}
            onInputChange={handleInputChange}
            onChange={(_, value) => {
              if (value && typeof value === 'string') {
                handleAddressSelect(value)
                setOpen(false)
                setHighlightedOption(null)
              }
            }}
            onHighlightChange={(_, option) => {
              setHighlightedOption(option)
            }}
            loading={isSearching}
            filterOptions={(options) => options} // Don't filter, use our custom options
            clearText='Clear address'
            disableClearable={!addressInput}
            renderOption={(props, option, { index }) => (
              <li
                {...props}
                role='option'
                aria-posinset={index + 1}
                aria-setsize={suggestions.length}
              >
                {option}
              </li>
            )}
            renderInput={({ slotProps, ...params }) => (
              <TextField
                id={params.id}
                disabled={params.disabled}
                fullWidth
                size='small'
                variant='outlined'
                placeholder='Enter address or coordinates (e.g., 40.7128, -74.0060)'
                InputProps={{
                  ref: slotProps?.input?.ref,
                  className: slotProps?.input?.className,
                  onMouseDown: slotProps?.input?.onMouseDown,
                  endAdornment: slotProps?.input?.endAdornment,
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Icon name='Search' />
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  ...(slotProps?.htmlInput ?? {}),
                  role: 'combobox',
                  'aria-autocomplete': 'list',
                  'aria-controls': 'inline-address-suggestions-listbox',
                  'aria-owns': 'inline-address-suggestions-listbox',
                  'aria-expanded': Boolean(open && suggestions.length > 0),
                  'aria-label':
                    'Search for an address. When suggestions appear, use arrow keys to navigate and Enter to select',
                }}
              />
            )}
            sx={{
              mb: 2,
              '& .MuiAutocomplete-clearIndicator': {
                visibility: addressInput ? 'visible' : 'hidden',
              },
            }}
            slotProps={{
              listbox: {
                id: 'inline-address-suggestions-listbox',
                role: 'listbox',
                'aria-label': 'Address suggestions',
              },
              clearIndicator: {
                tabIndex: 0,
                'aria-label': 'Clear address',
                onMouseDown: (e: React.MouseEvent) => {
                  e.preventDefault()
                },
              },
            }}
          />

          {/* Screen reader announcements */}
          <div aria-live='assertive' role='status' style={{ ...visuallyHidden }}>
            {isSearching ? 'Searching for addresses...' : ''}
            {!isSearching && open && suggestions.length > 0 && !highlightedOption
              ? `Found ${suggestions.length} suggestions. Use arrow keys to navigate and Enter to select`
              : ''}
          </div>
          <div aria-live='assertive' role='status' style={{ ...visuallyHidden }}>
            {highlightedOption}
          </div>

          {/* Current Location Display */}
          {isValidLocation && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon />
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  Selected Location:
                </Typography>
              </Box>
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                  mt: 0.5,
                }}
              >
                {displayAddress}
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                  fontFamily: 'monospace',
                  mt: 0.5,
                }}
              >
                {`<${markerPosition.lat.toFixed(6)}, ${markerPosition.lon.toFixed(6)}>`}
              </Typography>
            </Box>
          )}

          {/* Map */}
          <Box
            ref={mapContainer}
            sx={{
              height: '200px',
              width: '100%',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          />

          <Typography
            variant='caption'
            sx={{
              color: 'text.secondary',
              mt: 1,
              display: 'block',
            }}
          >
            Drag the marker to adjust your exact location
          </Typography>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', mt: -2, justifyContent: 'flex-end' }}>
            <Button
              variant='contained'
              size='small'
              onClick={handleSave}
              disabled={!isValidLocation}
            >
              Submit
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default LocationEntryInline
